import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

declare module "axios" {
  export interface AxiosRequestConfig {
    __retryCount?: number;
    __authRetry?: boolean;
    __skipTenantHeader?: boolean;
  }

  export interface InternalAxiosRequestConfig {
    __retryCount?: number;
    __authRetry?: boolean;
    __skipTenantHeader?: boolean;
  }
}

import { TENANT_HEADER } from "@/lib/auth/constants";
import { LOGIN_PATH, sanitizeInternalNextPath } from "@/lib/auth/protected-routes";
import { env } from "@/lib/env";
import { refresh as refreshAuthSession } from "@/modules/auth/api";
import { useAuthStore } from "@/modules/auth/store";

import { installMockApiIfEnabled } from "@/lib/mock/api/mock-api";

import { AppHttpError, normalizeAxiosError } from "./errors";

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 500;

function routerBasePath(): string {
  const rawBase = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
  return rawBase === "" ? "" : rawBase;
}

function stripRouterBase(path: string): string {
  const base = routerBasePath();
  if (!base || base === "/") {
    return path;
  }
  if (path === base) {
    return "/";
  }
  return path.startsWith(`${base}/`) ? path.slice(base.length) : path;
}

function redirectToLoginAfterAuthFailure(): void {
  if (typeof window === "undefined") {
    return;
  }
  const currentPath = stripRouterBase(window.location.pathname);
  const current = `${currentPath}${window.location.search}`;
  if (currentPath === LOGIN_PATH) {
    return;
  }
  const next = sanitizeInternalNextPath(current);
  window.location.assign(`${routerBasePath()}${LOGIN_PATH}?next=${encodeURIComponent(next)}&auth_error=1`);
}

function backoffMs(attempt: number): number {
  return Math.min(BASE_DELAY_MS * 2 ** attempt, 8000);
}

function shouldRetryAxiosError(error: AxiosError, retryCount: number): boolean {
  if (retryCount >= MAX_RETRIES) {
    return false;
  }
  const method = error.config?.method?.toUpperCase();
  if (method !== "GET") {
    return false;
  }
  const status = error.response?.status;
  return (
    error.code === "ECONNABORTED" ||
    error.code === "ERR_NETWORK" ||
    status === 429 ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const apiClient = axios.create({
  baseURL:
    env.useMock && !env.publicApiUrl ? "https://mock.api.local" : env.publicApiUrl || undefined,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30_000,
  withCredentials: true,
});

installMockApiIfEnabled(apiClient);

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { token, tenantId } = useAuthStore.getState();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (tenantId && config.headers && !config.__skipTenantHeader) {
      config.headers[TENANT_HEADER] = tenantId;
    }
    return config;
  },
  (error) => Promise.reject(normalizeAxiosError(error)),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const axError = error as AxiosError;
    const config = axError.config;

    if (!config) {
      const normalized = normalizeAxiosError(axError);
      if (axError.response?.status === 401) {
        useAuthStore.getState().clearSession();
        redirectToLoginAfterAuthFailure();
      }
      return Promise.reject(normalized);
    }

    const retryCount = config.__retryCount ?? 0;
    const normalized = normalizeAxiosError(axError);

    if (axError.response?.status === 401 && !config.__authRetry) {
      config.__authRetry = true;
      const refreshed = await refreshAuthSession();
      if (refreshed?.access_token) {
        return apiClient.request(config);
      }
      useAuthStore.getState().clearSession();
      redirectToLoginAfterAuthFailure();
      return Promise.reject(normalized);
    }

    if (shouldRetryAxiosError(axError, retryCount)) {
      const nextCount = retryCount + 1;
      config.__retryCount = nextCount;
      await sleep(backoffMs(retryCount));
      return apiClient.request(config);
    }

    if (axError.response?.status === 401) {
      useAuthStore.getState().clearSession();
      redirectToLoginAfterAuthFailure();
    }
    return Promise.reject(normalized);
  },
);

export function isAppHttpError(error: unknown): error is AppHttpError {
  return error instanceof AppHttpError;
}
